import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { ExternalLink, Loader2, RefreshCw } from 'lucide-react';

const News = () => {
  const { t, i18n } = useTranslation();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('general');

  const categories = [
    { id: 'general', label: t('news.allCategories') },
    { id: 'education', label: t('news.education') },
    { id: 'technology', label: t('news.technology') },
    { id: 'science', label: t('news.science') },
    { id: 'world', label: t('news.world') },
    { id: 'sports', label: t('news.sports') },
    { id: 'entertainment', label: t('news.entertainment') },
    { id: 'business', label: t('news.business') },
  ];

  const fetchNews = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/news`, {
        params: { category, lang: i18n.language }
      });
      setNews(response.data.news || []);
    } catch (error) {
      console.error('News fetch error:', error);
      setNews([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
  }, [category, i18n.language]);

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // Strip HTML from description
  const stripHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  return (
    <div className="pt-16 min-h-screen bg-[#050505]" data-testid="news-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <span className="text-sm uppercase tracking-[0.2em] font-semibold text-zinc-500">
              {t('nav.news')}
            </span>
            <h1 className="font-outfit text-4xl md:text-5xl font-black text-white mt-2">
              {t('news.title')} <span className="text-red-500">Feed</span>
            </h1>
            <p className="text-zinc-400 mt-2">{t('news.subtitle')}</p>
          </div>
          <Button
            onClick={fetchNews}
            variant="outline"
            className="border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-sm"
            data-testid="refresh-news-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((cat) => (
            <Button
              key={cat.id}
              onClick={() => setCategory(cat.id)}
              variant={category === cat.id ? 'default' : 'outline'}
              className={`rounded-sm ${
                category === cat.id
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800'
              }`}
              data-testid={`category-${cat.id}`}
            >
              {cat.label}
            </Button>
          ))}
        </div>

        {/* News Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-12 h-12 text-red-500 animate-spin" />
          </div>
        ) : news.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-zinc-500 text-lg">{t('news.noNews')}</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {news.map((item, idx) => (
              <a
                key={idx}
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className={`card-hover bg-[#121212] rounded-sm overflow-hidden group animate-fadeIn stagger-${(idx % 5) + 1}`}
                data-testid={`news-item-${idx}`}
              >
                {item.image && (
                  <div className="h-48 overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
                <div className="p-5">
                  <div className="flex items-center gap-2 text-xs text-zinc-500 mb-3">
                    {item.source && (
                      <>
                        <span className="font-semibold text-red-500">{item.source}</span>
                        <span>•</span>
                      </>
                    )}
                    <span>{formatDate(item.pubDate)}</span>
                  </div>
                  <h3 className="font-semibold text-white mb-3 line-clamp-2 group-hover:text-red-500 transition-colors">
                    {item.title}
                  </h3>
                  {item.description && (
                    <p className="text-sm text-zinc-400 line-clamp-3 mb-4">
                      {stripHtml(item.description).substring(0, 150)}...
                    </p>
                  )}
                  <span className="inline-flex items-center gap-1 text-sm text-red-500 font-medium">
                    {t('news.readFullArticle')}
                    <ExternalLink className="w-3 h-3" />
                  </span>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default News;
